/**
 * useCreateChannel Hook
 *
 * Mutation hook for creating a new channel in a Microsoft Team.
 *
 * Unlike the data fetching hooks, this is a mutation hook that:
 * - Returns a function to trigger the mutation
 * - Provides loading and error states
 * - Returns the created channel on success
 * - Provides a reset function to clear state
 */

import { useState, useCallback } from 'react';
import { TenantApiService } from '../services/tenantApi';
import type { CreateChannelRequest, CreatedChannel } from '../types/sharepoint';

interface UseCreateChannelOptions {
  tenantId: string;
  teamId: string;
}

interface UseCreateChannelResult {
  createChannel: (data: CreateChannelRequest) => Promise<CreatedChannel | null>;
  isLoading: boolean;
  error: string | null;
  createdChannel: CreatedChannel | null;
  reset: () => void;
}

/**
 * Hook to create a new channel in a Microsoft Team.
 *
 * @param options.tenantId - The managed tenant ID
 * @param options.teamId - The Microsoft Team ID
 * @returns Object with createChannel function, loading state, error, createdChannel, and reset function
 */
export function useCreateChannel({
  tenantId,
  teamId,
}: UseCreateChannelOptions): UseCreateChannelResult {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [createdChannel, setCreatedChannel] = useState<CreatedChannel | null>(null);

  const createChannel = useCallback(async (data: CreateChannelRequest): Promise<CreatedChannel | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const channel = await TenantApiService.createChannel(tenantId, teamId, data);
      setCreatedChannel(channel);
      return channel;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create channel';
      setError(errorMessage);
      setCreatedChannel(null);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [tenantId, teamId]);

  const reset = useCallback(() => {
    setIsLoading(false);
    setError(null);
    setCreatedChannel(null);
  }, []);

  return {
    createChannel,
    isLoading,
    error,
    createdChannel,
    reset,
  };
}
